import type { BasicDie, ContainerEntry } from './types';
import { BALANCE } from '../data/balance';
import { RARITY_ORDER } from '../data/machines';
import type { NodeLevels } from './skilltree';
import { nodeLevel } from './skilltree';

/**
 * Le **Gobelet** et son contenu — COMMENTS v0.4.1 §1.2.
 *
 * Le contenant n'est jamais rempli à la main : il est **entièrement dérivé** de
 * l'arbre de talents, qui survit aux runs. Cinq nœuds le gouvernent — les trois
 * **contenants** s'additionnent (DECISIONS §D.18), les deux **formes** se
 * partagent le total :
 *  - `ars_goblet` (*Gobelet*) : 2 dés Basiques par niveau (2 → 10) ;
 *  - `ars_bowl` (*Bol*) : +10 dés par niveau (→ +50) ;
 *  - `ars_bucket` (*Seau*) : +100 dés par niveau (→ +500), soit 560 au total ;
 *  - `ars_dodeca` (*Dodécaédrique*) : 20 % des dés deviennent des d12 par niveau ;
 *  - `ars_hex` (*Contenant hexagonal*) : 20 % des dés deviennent des d6 par
 *    niveau, arrondi au plus proche (Q28) ;
 *  - v0.26.0 : `ars_barrow` (*Brouette*) : +1 000 dés par niveau (→ +5 000,
 *    5 560 au total) et `ars_icosa` (*Icosaèdrique*) : 20 % de d20 par niveau.
 *
 * Ordre de service : **d20 d'abord (A72-8), puis d12, puis d6, puis d4** ;
 * chaque part est arrondie au plus proche et plafonnée par ce qui reste.
 *
 * Ce module remplace `core/prestige.ts` (v0.4.x) : les Âmes et l'arbre de
 * prestige ont disparu, le contenant est devenu une récompense de l'Arsenal.
 * Fonctions **pures** : elles ne lisent que des niveaux de nœuds.
 */

/** Le joueur possède-t-il un contenant (nœud *Gobelet*) ? */
export function hasContainer(levels: NodeLevels): boolean {
  return nodeLevel(levels, 'ars_goblet') >= 1;
}

/**
 * Nombre de dés Basiques du contenant : `2 × Gobelet + 10 × Bol + 100 × Seau
 * + 1 000 × Brouette` (0 sans le *Gobelet*, 5 560 avec les quatre contenants au
 * maximum). Le contenant étant toujours plein, « capacité » et « contenu » se
 * confondent.
 */
export function containerCapacity(levels: NodeLevels): number {
  if (!hasContainer(levels)) return 0;
  return (
    BALANCE.gobletDicePerLevel * nodeLevel(levels, 'ars_goblet') +
    BALANCE.nodeBowlDicePerLevel * nodeLevel(levels, 'ars_bowl') +
    BALANCE.nodeBucketDicePerLevel * nodeLevel(levels, 'ars_bucket') +
    BALANCE.nodeBarrowDicePerLevel * nodeLevel(levels, 'ars_barrow')
  );
}

/**
 * Nom du contenant affiché au joueur : le plus grand contenant débloqué —
 * *Brouette* dès le niveau 1 de `ars_barrow` (COMMENTS v0.25.0 §1.2), sinon
 * *Seau*, *Bol*, *Gobelet*. `null` sans contenant.
 */
export function containerName(levels: NodeLevels): string | null {
  if (!hasContainer(levels)) return null;
  if (nodeLevel(levels, 'ars_barrow') >= 1) return 'Brouette';
  if (nodeLevel(levels, 'ars_bucket') >= 1) return 'Seau';
  if (nodeLevel(levels, 'ars_bowl') >= 1) return 'Bol';
  return 'Gobelet';
}

/** Part d'un nœud de forme, arrondie au plus proche et bornée par `room`. */
function shareOf(count: number, room: number, level: number, perLevel: number): number {
  if (count <= 0 || room <= 0 || level <= 0) return 0;
  return Math.max(0, Math.min(room, Math.round(count * perLevel * level)));
}

/**
 * Nombre de d20 du contenant — *Icosaèdrique* (A72-8) : `round(dés × 0,2 ×
 * niveau)`, borné au nombre de dés. Servi **avant** les d12.
 */
export function containerD20Count(levels: NodeLevels): number {
  const count = containerCapacity(levels);
  return shareOf(count, count, nodeLevel(levels, 'ars_icosa'), BALANCE.nodeIcosaSharePerLevel);
}

/**
 * Nombre de d12 du contenant — *Dodécaédrique* : `round(dés × 0,2 × niveau)`,
 * borné par ce que les d20 laissent. Servi **avant** les d6.
 */
export function containerD12Count(levels: NodeLevels): number {
  const count = containerCapacity(levels);
  const room = count - containerD20Count(levels);
  return shareOf(count, room, nodeLevel(levels, 'ars_dodeca'), BALANCE.nodeDodecaSharePerLevel);
}

/**
 * Nombre de d6 du contenant — *Contenant hexagonal* : `round(dés × 0,2 × niveau)`,
 * borné par ce que les d20 et les d12 laissent. Le reste est en d4.
 */
export function containerD6Count(levels: NodeLevels): number {
  const count = containerCapacity(levels);
  const room = count - containerD20Count(levels) - containerD12Count(levels);
  return shareOf(count, room, nodeLevel(levels, 'ars_hex'), BALANCE.nodeHexSharePerLevel);
}

/**
 * Contenu **dérivé** du contenant : les d20 d'abord, puis les d12, les d6, les d4.
 * Les dés Basiques n'ont pas de rareté ; le champ `rarity` reste à `'common'`
 * pour la vue de combat, qui l'utilise pour choisir la texture du dé.
 */
export function containerDice(levels: NodeLevels): BasicDie[] {
  const count = containerCapacity(levels);
  if (count <= 0) return [];
  const d20 = containerD20Count(levels);
  const d12 = containerD12Count(levels);
  const d6 = containerD6Count(levels);
  const out: BasicDie[] = [];
  for (let i = 0; i < d20; i++) out.push({ sides: 20, rarity: 'common' });
  for (let i = 0; i < d12; i++) out.push({ sides: 12, rarity: 'common' });
  for (let i = 0; i < d6; i++) out.push({ sides: 6, rarity: 'common' });
  for (let i = d20 + d12 + d6; i < count; i++) out.push({ sides: 4, rarity: 'common' });
  return out;
}

/**
 * Dés Basiques **supplémentaires** d'un lancer de tractopelle (A72-17) : `n`
 * dés de **même composition** que le contenant — chaque nombre de faces reçoit
 * sa part `n × part`, arrondie par la méthode du plus fort reste pour que le
 * total fasse exactement `n`. Renvoie le nombre de dés par nombre de faces
 * (ordre d20, d12, d6, d4) ; vide sans contenant ou pour `n ≤ 0`.
 */
export function backhoeComposition(
  container: readonly BasicDie[],
  n: number,
): Array<{ sides: number; count: number }> {
  const total = container.length;
  const wanted = Math.max(0, Math.floor(n));
  if (total === 0 || wanted === 0) return [];
  const bySides = new Map<number, number>();
  for (const die of container) bySides.set(die.sides, (bySides.get(die.sides) ?? 0) + 1);
  const order = [...bySides.keys()].sort((a, b) => b - a);
  const parts = order.map((sides) => {
    const exact = (wanted * (bySides.get(sides) ?? 0)) / total;
    return { sides, count: Math.floor(exact), rest: exact - Math.floor(exact) };
  });
  let missing = wanted - parts.reduce((s, p) => s + p.count, 0);
  // Plus forts restes d'abord ; à égalité, le plus gros dé (ordre stable).
  const byRest = parts.slice().sort((a, b) => b.rest - a.rest);
  for (let i = 0; missing > 0 && i < byRest.length; i++, missing--) byRest[i].count += 1;
  return parts.filter((p) => p.count > 0).map(({ sides, count }) => ({ sides, count }));
}

/** Force d'un dé Basique pour l'ordre d'affichage : nombre de faces, puis rareté. */
export function basicStrength(die: BasicDie): number {
  return die.sides * 100 + RARITY_ORDER.indexOf(die.rarity);
}

/** Contenu du contenant regroupé par nombre de faces, du plus fort au plus faible. */
export function containerSummary(levels: NodeLevels): ContainerEntry[] {
  const map = new Map<string, ContainerEntry>();
  for (const die of containerDice(levels)) {
    const key = `${die.sides}:${die.rarity}`;
    const entry = map.get(key);
    if (entry) entry.count += 1;
    else map.set(key, { sides: die.sides, rarity: die.rarity, count: 1 });
  }
  return Array.from(map.values()).sort((a, b) => basicStrength(b) - basicStrength(a));
}
